import type { DateTime, DurationLikeObject } from 'luxon';
import { RRule, RRuleSet } from 'rrule';
import {
  fromRRuleDate,
  getRecurrenceZone,
  serializeEventKey,
  toRRuleAnchor,
} from '@/features/events/lib/eventInstance.lib';
import {
  isInstanceHidden,
  isInstanceInheritedHidden,
} from '@/features/events/lib/eventVisibility.lib';
import type { DecoratedExperienceEvent } from '@/features/events/types';

/**
 * Cap on the rows one source lists. A year of a daily series is ~365 rows by
 * itself, so a feed with a few of them is well past what a flat list is good
 * for; `isTruncated` lets the list say so rather than trimming silently.
 */
export const MAX_SOURCE_EVENT_INSTANCES = 400;

/**
 * One occurrence of one of a source's events.
 *
 * The series is carried whole rather than copied per occurrence, so a row can
 * write to the event it belongs to — its metadata, its id — while `startDate`
 * names which occurrence the row is. Both are needed together to hide one
 * occurrence (see eventVisibility.lib's isInstanceHidden/setInstanceHidden).
 */
export type SourceEventInstance = {
  event: DecoratedExperienceEvent;
  /** Occurrence start, in the same display zone as `event.startDate`. */
  startDate: DateTime;
  /** Occurrence end. All-day ends stay exclusive, as they are on the event. */
  endDate: DateTime;
  isRecurring: boolean;
  /** Stable per-occurrence identity, for React keys. */
  key: string;
};

type Occurrence = Pick<SourceEventInstance, 'startDate' | 'endDate'>;

/**
 * The event's length, in the unit an occurrence start can be advanced by.
 * All-day spans are whole calendar days: re-adding them as minutes would land a
 * DST-crossing occurrence at 23:00 or 01:00 instead of the next local midnight.
 */
const getDuration = (event: DecoratedExperienceEvent): DurationLikeObject =>
  event.isAllDay
    ? { days: event.endDate.diff(event.startDate, 'days').days }
    : { minutes: event.endDate.diff(event.startDate, 'minutes').minutes };

const getOccurrences = (
  event: DecoratedExperienceEvent,
  rangeStart: DateTime,
  rangeEnd: DateTime
): Occurrence[] => {
  const seriesOccurrence = {
    startDate: event.startDate,
    endDate: event.endDate,
  };

  if (!event.rrule) {
    return [seriesOccurrence];
  }

  const duration = getDuration(event);
  // `startDate` was converted to the viewer's display zone when the event was
  // decorated (see createEntryDate), so it carries the zone the expanded
  // occurrences have to be rendered in.
  const displayZone = event.startDate.zone;

  try {
    // Anchored exactly as the planner/printable expander anchors it
    // (eventInstance.lib), so the panel lists the instants those surfaces render.
    const recurrenceZone = getRecurrenceZone(event);
    const options = RRule.parseString(event.rrule);
    options.dtstart = toRRuleAnchor(event.startDate, recurrenceZone).toJSDate();

    // An open-ended rule is bounded at the end of the window being listed. The
    // `.all()`-based expanders measure RECURRENCE_WINDOW from the far edge of
    // the range they render (getFallbackUntil), which reaches the same
    // occurrences; here the window is already the query's own range and
    // `between` clips to it, so `until` can sit exactly on its end rather than
    // a further 18 months out.
    if (!options.until && !options.count) {
      options.until = toRRuleAnchor(rangeEnd, recurrenceZone).toJSDate();
    }

    const rruleSet = new RRuleSet();
    rruleSet.rrule(new RRule(options));

    // The lower bound reaches back by one occurrence's length so an occurrence
    // that started before the window but hasn't finished is still generated;
    // finished ones are dropped by the caller. `inc` is required at the lower
    // bound, where an all-day occurrence sits exactly on midnight.
    const dates = rruleSet.between(
      toRRuleAnchor(rangeStart.minus(duration), recurrenceZone).toJSDate(),
      toRRuleAnchor(rangeEnd, recurrenceZone).toJSDate(),
      true
    );

    return dates.map((date) => {
      const startDate = fromRRuleDate(date, recurrenceZone).setZone(
        displayZone
      );
      return { startDate, endDate: startDate.plus(duration) };
    });
  } catch {
    // An unparsable rule shouldn't empty the list — fall back to the series' own
    // dates, which is what the row showed before instances were expanded.
    return [seriesOccurrence];
  }
};

/**
 * The occurrences a source's events contribute to `[rangeStart, rangeEnd]`,
 * chronologically, with recurring series expanded into one entry per occurrence.
 *
 * Anything already finished is dropped: the panel lists what is still to come.
 * An event that is under way keeps its row — an in-progress trip is not past —
 * so the test is on the occurrence's end, not its start. All-day ends are
 * exclusive, which makes `>` the right comparison for both kinds.
 *
 * Hidden occurrences are dropped here, *before* the row cap, unless `showHidden`
 * asks for them: filtering after the cap would spend the budget on rows nobody
 * asked to see and lose visible occurrences behind them. `hasHidden` reports
 * whether the window holds any hidden occurrence at all — computed over the
 * uncapped, unfiltered set, so the panel's reveal toggle appears whenever there
 * is something to reveal. Occurrences hidden upstream of this page are not
 * this page's to reveal: they are left out entirely and never counted.
 */
export const expandSourceEventInstances = (
  events: DecoratedExperienceEvent[],
  rangeStart: DateTime,
  rangeEnd: DateTime,
  showHidden = false
): {
  instances: SourceEventInstance[];
  isTruncated: boolean;
  hasHidden: boolean;
} => {
  const instances: SourceEventInstance[] = [];
  let hasHidden = false;

  events.forEach((event) => {
    getOccurrences(event, rangeStart, rangeEnd).forEach(
      ({ startDate, endDate }) => {
        if (endDate <= rangeStart || startDate > rangeEnd) {
          return;
        }

        if (isInstanceInheritedHidden(event, startDate)) {
          return;
        }

        if (isInstanceHidden(event, startDate)) {
          hasHidden = true;
          if (!showHidden) {
            return;
          }
        }

        instances.push({
          event,
          startDate,
          endDate,
          isRecurring: !!event.rrule,
          key: serializeEventKey(event.id, startDate),
        });
      }
    );
  });

  instances.sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis());

  return {
    instances: instances.slice(0, MAX_SOURCE_EVENT_INSTANCES),
    isTruncated: instances.length > MAX_SOURCE_EVENT_INSTANCES,
    hasHidden,
  };
};
